import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { firstOrNull } from 'common/utils/array';
import { useVenueAdsFreeTrialConfig } from 'modules/campaigns';
import { useAdCampaignVenueConfiguration } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignVenueConfiguration';
import { useVenueAdCampaignMutations } from 'modules/campaigns/ad-campaign/hooks/useVenueAdCampaignMutations';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { VenueAdCampaignConfigFormValues, VenueAdCampaignFormValues } from 'modules/campaigns/types';

export const getAdFormDefaultValues = ({
  venueAdsConfig,
  isFreeTrial,
}: {
  venueAdsConfig: VenueAdCampaignConfigFormValues | undefined;
  isFreeTrial: boolean;
}): VenueAdCampaignFormValues => ({
  campaignType: CampaignType.ADS,
  agreementTermsAndConditions: false,
  budgetAmount: venueAdsConfig?.budgetOptions.find(o => o.type === 'recommended')?.value ?? 0,
  startDate: DateTime.local().toISO(),
  budgetType: 'recommended',
  bidStrategy: 'auto',
  scheduleStrategy: 'alwaysOn',
  isFreeTrial,
});

export const useVenueAdCampaignFormValues = ({ isEnabled }: { isEnabled: boolean }) => {
  const { merchantId, country, currency, venues } = useCampaignModuleContext();
  const venue = firstOrNull(venues);
  const venueId = venue?.id ?? '';

  const areQueriesEnabled = isEnabled && !!venueId;

  const { createVenueAdCampaign } = useVenueAdCampaignMutations({ venueId });

  const handleSubmitAdCampaign = (values: VenueAdCampaignFormValues) => {
    return createVenueAdCampaign.mutate({ ...values, country, currency });
  };

  const { data: venueAdsConfig, isLoading: isAdConfigLoading } = useAdCampaignVenueConfiguration({
    venueId,
    country,
    enabled: areQueriesEnabled,
  });

  const { config: adsFreeTrialConfig, isLoading: isFreeTrialLoading } = useVenueAdsFreeTrialConfig({
    venueId,
    country,
    merchantId,
    city: venue?.city,
    productLine: venue?.product_line,
    enabled: areQueriesEnabled,
  });

  const defaultValues = useMemo(
    () => getAdFormDefaultValues({ isFreeTrial: !!adsFreeTrialConfig, venueAdsConfig }),
    [adsFreeTrialConfig, venueAdsConfig],
  );

  return {
    adConfig: venueAdsConfig,
    freeTrialConfig: adsFreeTrialConfig,
    defaultValues,
    submitForm: handleSubmitAdCampaign,
    isLoading: isAdConfigLoading || isFreeTrialLoading,
    isFormSubmissionLoading: createVenueAdCampaign.isLoading,
  };
};
